/**
 * Deterministic two-column masonry for the project grids.
 *
 * Why this exists instead of relying on CSS grid auto-placement:
 * - filtered cards can disappear/reappear in any source-order combination;
 * - auto-placement can preserve large holes after multi-filter changes;
 * - explicit column + row-start placement makes every refresh deterministic.
 *
 * Safety contract:
 * - mobile stays a normal one-column CSS Grid;
 * - desktop measurements always start from the safe normal-grid state;
 * - only visible cards participate in masonry placement;
 * - every refresh fully clears stale placement styles before re-measuring.
 */

export const computeMasonryPlacements = (heights, {
    columns = 2,
    rowHeight = 8,
    verticalGap = 72,
} = {}) => {
    const safeColumns = Math.max(1, Math.floor(columns));
    const safeRow = Math.max(1, Number(rowHeight) || 8);
    const gapRows = Math.max(0, Math.ceil((Number(verticalGap) || 0) / safeRow));
    const columnEnds = Array.from({ length: safeColumns }, () => 1);

    return heights.map((rawHeight) => {
        const height = Math.max(1, Number(rawHeight) || 1);
        const rowSpan = Math.max(1, Math.ceil(height / safeRow));
        let columnIndex = 0;

        for (let index = 1; index < columnEnds.length; index += 1) {
            if (columnEnds[index] < columnEnds[columnIndex]) columnIndex = index;
        }

        const rowStart = columnEnds[columnIndex];
        columnEnds[columnIndex] = rowStart + rowSpan + gapRows;

        return {
            column: columnIndex + 1,
            rowStart,
            rowSpan,
        };
    });
};

export function createMasonryGrid(grid, {
    cardSelector = '[data-project-card]',
    breakpoint = 768,
    rowHeight = 8,
    verticalGap = 96,
} = {}) {
    if (!grid) return null;

    let frame = 0;
    let validationFrame = 0;
    let destroyed = false;
    let lastGridWidth = 0;
    const media = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const observers = new Map();

    grid.style.setProperty('--led-masonry-gap', `${verticalGap}px`);
    grid.style.setProperty('--led-masonry-row', `${rowHeight}px`);

    const cards = () => [...grid.querySelectorAll(cardSelector)];
    const visibleCards = () => cards().filter((card) => !card.hidden && card.getClientRects().length);

    const clearCardPlacement = (card) => {
        card.style.removeProperty('grid-column-start');
        card.style.removeProperty('grid-row-start');
        card.style.removeProperty('grid-row-end');
        card.style.removeProperty('align-self');
    };

    const reset = () => {
        grid.removeAttribute('data-masonry-ready');
        grid.removeAttribute('data-masonry-fallback');
        cards().forEach(clearCardPlacement);
    };

    const hasOverlap = () => {
        const rects = visibleCards().map((card) => card.getBoundingClientRect());
        for (let i = 0; i < rects.length; i += 1) {
            const a = rects[i];
            for (let j = i + 1; j < rects.length; j += 1) {
                const b = rects[j];
                const sameColumn = a.left < b.right - 1 && a.right > b.left + 1;
                const verticalCollision = a.top < b.bottom - 1 && a.bottom > b.top + 1;
                if (sameColumn && verticalCollision) return true;
            }
        }
        return false;
    };

    const layout = () => {
        frame = 0;
        if (destroyed) return false;

        window.cancelAnimationFrame(validationFrame);
        validationFrame = 0;

        const width = grid.getBoundingClientRect().width;
        const allCards = cards();
        if (!media.matches || !allCards.length || width < 2) {
            lastGridWidth = width;
            reset();
            return false;
        }
        lastGridWidth = width;

        // Always remove every previous row/column assignment before measuring.
        // This is the important part for repeated filter / reset cycles.
        reset();
        allCards.forEach((card) => { card.style.alignSelf = 'start'; });

        const currentVisible = visibleCards();
        const heights = currentVisible.map((card) => card.getBoundingClientRect().height);
        const invalid = heights.some((height) => !Number.isFinite(height) || height < 2);
        if (invalid) {
            request();
            return false;
        }

        const placements = computeMasonryPlacements(heights, {
            columns: 2,
            rowHeight,
            verticalGap,
        });

        currentVisible.forEach((card, index) => {
            const placement = placements[index];
            card.style.gridColumnStart = String(placement.column);
            card.style.gridRowStart = String(placement.rowStart);
            card.style.gridRowEnd = `span ${placement.rowSpan}`;
        });
        grid.dataset.masonryReady = 'true';

        // Validate what the browser actually painted. If a future browser/CSS
        // change ever violates the placement contract, fail safe to normal grid.
        validationFrame = window.requestAnimationFrame(() => {
            validationFrame = 0;
            if (destroyed || grid.dataset.masonryReady !== 'true') return;
            if (!hasOverlap()) return;
            grid.dataset.masonryFallback = 'true';
            grid.removeAttribute('data-masonry-ready');
            cards().forEach(clearCardPlacement);
        });

        return true;
    };

    const request = () => {
        if (destroyed) return;
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
            frame = window.requestAnimationFrame(layout);
        });
    };

    const observeCards = () => {
        cards().forEach((card) => {
            if (observers.has(card)) return;
            const observer = new ResizeObserver(request);
            observer.observe(card);
            observers.set(card, observer);

            card.querySelectorAll('img').forEach((image) => {
                if (!image.complete) image.addEventListener('load', request, { once: true });
                image.decode?.().then(request).catch(() => {});
            });
        });
    };

    const refresh = () => {
        observeCards();
        request();
    };

    const layoutNow = () => {
        observeCards();
        window.cancelAnimationFrame(frame);
        frame = 0;
        return layout();
    };

    let observedGridWidth = 0;
    const gridObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect?.width || grid.getBoundingClientRect().width;
        if (Math.abs(width - observedGridWidth) < 0.5) return;
        observedGridWidth = width;
        if (Math.abs(width - lastGridWidth) > 0.5) request();
    });
    gridObserver.observe(grid);

    media.addEventListener?.('change', refresh);
    window.addEventListener('orientationchange', refresh, { passive: true });
    document.fonts?.ready?.then(refresh).catch?.(() => {});
    refresh();

    return {
        refresh,
        layoutNow,
        destroy() {
            destroyed = true;
            window.cancelAnimationFrame(frame);
            window.cancelAnimationFrame(validationFrame);
            gridObserver.disconnect();
            observers.forEach((observer) => observer.disconnect());
            observers.clear();
            media.removeEventListener?.('change', refresh);
            window.removeEventListener('orientationchange', refresh);
            reset();
        },
    };
}
